import {
    ClinicPatientType,
    ClinicPreviewDayType,
    ClinicProgressOverviewType,
    userType,
} from "../DTO";

export const isClinicUser = (user?: userType | null) => {
    return Boolean(
        user?.role?.some((roleItem) => roleItem?.name?.toLowerCase() === "clinic"),
    );
};

export const getClinicSignupStatus = (patient: ClinicPatientType) => {
    return patient.userId ? "Signed up" : "Not signed up yet";
};

export const getClinicLinkedUserStatus = (patient: ClinicPatientType) => {
    return patient.userId ? "User created" : "No user created";
};

export const getClinicReferralStage = (patient: ClinicPatientType) => {
    if (!patient.userId) {
        return {
            label: "Waiting for patient signup",
            tone: "muted",
        };
    }

    if (patient.registerState === "finished") {
        return {
            label: "Finished",
            tone: "success",
        };
    }

    if (patient.registerState === "preparing") {
        return {
            label: "In progress",
            tone: "primary",
        };
    }

    return {
        label: "Signed up",
        tone: "info",
    };
};

export const formatClinicDate = (value?: string | null) => {
    if (!value) {
        return "-";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString();
};

export const humanizeClinicKey = (key: string) => {
    return key
        .replace(/_/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const getClinicDisplayName = (patient: ClinicPatientType) => {
    if (patient.fullName) {
        return patient.fullName;
    }

    return `${patient.firstName || ""} ${patient.lastName || ""}`.trim() || "Patient";
};

export const getClinicDiseaseLabel = (patient: ClinicPatientType) => {
    if (patient.diseaseType === "type2") {
        return "Type 2 Diabetes";
    }

    return patient.diseaseType ? humanizeClinicKey(patient.diseaseType) : "Not set";
};

export const getClinicLightTone = (light?: string | null) => {
    if (light === "green") {
        return "success" as const;
    }

    if (light === "amber" || light === "yellow") {
        return "warning" as const;
    }

    if (light === "red") {
        return "danger" as const;
    }

    return "muted" as const;
};

export const getClinicOverviewStatus = (
    overview?: ClinicProgressOverviewType | null,
) => {
    if (!overview?.hasActivePlan) {
        return {
            label: "No active plan",
            tone: "muted" as const,
        };
    }

    const light = overview.latestDaily?.display?.light;

    if (light) {
        return {
            label: overview.latestDaily?.display?.title || "Daily status",
            tone: getClinicLightTone(light),
        };
    }

    return {
        label: "Active plan",
        tone: "info" as const,
    };
};

export const getClinicTrendLabel = (direction?: string | null) => {
    if (!direction) {
        return "Stable";
    }

    if (direction === "improving") {
        return "Improving";
    }

    if (direction === "declining") {
        return "Needs attention";
    }

    return humanizeClinicKey(direction);
};

export const getClinicDayTone = (day?: ClinicPreviewDayType | null) => {
    if (!day || day.isLock || day.percentage === null) {
        return "muted" as const;
    }

    if (day.percentage >= 80) {
        return "success" as const;
    }

    if (day.percentage >= 60) {
        return "warning" as const;
    }

    return "danger" as const;
};
